import { format } from 'date-fns';
import {
  MultiSelectOption,
  ScheduleFilters,
  AttributeDefinition,
} from '@dispatch/shared/types';

export interface MapSchedulerFiltersOptions {
  taskFilters: Record<string, any>;
  resourceFilters: Record<string, any>;
  attributeDefinitions: AttributeDefinition[];
  dateRange: { startDate: Date; endDate: Date };
  timeZone: string;
  status?: string;
  pageSize?: number;
  isAdjuster?: boolean;
  currentUserResourceId?: string;
}

/**
 * Extracts IDs from an array of MultiSelectOptions or strings.
 */
function extractIds(
  items: MultiSelectOption[] | string[] | undefined
): string[] | undefined {
  if (!items || items.length === 0) return undefined;
  return items.map((item) =>
    typeof item === 'string' ? item : String(item.value)
  );
}

/**
 * Projects a [from, to] date-range tuple into scalar `*From`/`*To` keys the API
 * reads. The form state owns the tuple; the modal's submit path never derives
 * the scalars, so mapping does it here (mirrors v1's `handleLossDateFilters`).
 */
function deriveDateRange(
  tuple: unknown,
  fromKey: string,
  toKey: string
): Record<string, string> {
  if (!Array.isArray(tuple)) return {};
  const derived: Record<string, string> = {};
  if (tuple[0]) derived[fromKey] = format(new Date(tuple[0]), 'yyyy-MM-dd');
  if (tuple[1]) derived[toKey] = format(new Date(tuple[1]), 'yyyy-MM-dd');
  return derived;
}

/**
 * Maps UI filter context values to API-expected filter keys for the schedule endpoint.
 *
 * Key mappings (matches what schedule.service.ts hasResourceFilters expects):
 *   teams                → assignedResourceTeamIds
 *   offices              → assignedResourceOfficeIds
 *   groups               → assignedResourceGroupIds
 *   roles                → assignedResourceRoleIds
 *   company attrs        → resourceAttributes (structured array)
 *   employmentType       → isContingent (+ contingentCompanies when contingent)
 *
 * Always sends `hideCancelledTasks: false` — v2 scheduler doesn't expose the
 * derived toggle v1 TasksList uses, so the payload carries a stable default.
 */

/**
 * v1 parity (ResourcesList.tsx `getIsContingent`): maps the UI's
 * employmentType string onto the boolean `isContingent` the API reads.
 * Returns `undefined` (rather than null) so the key is omitted from the
 * payload entirely when the filter isn't set.
 */
function getIsContingent(employmentType: unknown): boolean | undefined {
  if (employmentType === 'contingent') return true;
  if (employmentType === 'staff') return false;
  return undefined;
}

export function mapSchedulerFilters({
  taskFilters,
  resourceFilters,
  attributeDefinitions,
  dateRange,
  timeZone,
  status,
  pageSize = 10,
  isAdjuster = false,
  currentUserResourceId,
}: MapSchedulerFiltersOptions): ScheduleFilters {
  const merged = { ...taskFilters, ...resourceFilters };

  // Exclude properties that shouldn't affect scheduler:
  // - forceScheduleDates: TasksListPage-specific
  // - startDate/endDate: Complex Loss uses these (different from scheduleStartDate/scheduleEndDate)
  // - complexResources: Complex Loss-specific
  // - lossDate/appointmentDate/noticeDate/receivedDate: UI-only [from, to] tuples.
  //   The API reads the derived scalars (*DateFrom/*DateTo) — the tuples would be
  //   noise on the payload (v1 stripped them too).
  // - *DateFrom/*DateTo: strip saved scalars too, then rebuild from the tuple.
  //   If the tuple was cleared but a pre-saved shape still carries scalars, this
  //   prevents the stale values from leaking through `cleanedFilters`.
  // - employmentType/contingentCompanies: pulled out so the UI's employmentType
  //   string doesn't leak to the API. They're rebuilt below as `isContingent`
  //   (+ `contingentCompanies` when contingent).
  const {
    forceScheduleDates,
    startDate,
    endDate,
    complexResources,
    lossDate,
    appointmentDate,
    noticeDate,
    receivedDate,
    lossDateFrom,
    lossDateTo,
    appointmentDateFrom,
    appointmentDateTo,
    noticeDateFrom,
    noticeDateTo,
    receivedDateFrom,
    receivedDateTo,
    offices,
    groups,
    resourceTeams,
    teams,
    roles,
    employmentType,
    contingentCompanies,
    ...relevantFilters
  } = merged;

  const isContingent = getIsContingent(employmentType);

  // Build resourceAttributes structured array from company attribute filter values
  // (matches TasksList.tsx pattern)
  const resourceAttributes = attributeDefinitions
    .filter((attr) => {
      const val = merged[attr.displayName];
      return Array.isArray(val) ? val.length > 0 : !!val;
    })
    .map((attr) => ({
      attributeDefinition: attr.displayName,
      values: merged[attr.displayName],
    }));

  // Remove raw company attribute keys so they don't leak to API
  const cleanedFilters = { ...relevantFilters };
  attributeDefinitions.forEach((attr) => {
    delete cleanedFilters[attr.displayName];
  });

  // Derive *From/*To scalars from the tuple keys. Tuple is authoritative —
  // the saved scalars were already stripped above, so if a tuple is absent
  // the corresponding scalar simply won't appear on the payload (cleared).
  const dateScalars = {
    ...deriveDateRange(lossDate, 'lossDateFrom', 'lossDateTo'),
    ...deriveDateRange(
      appointmentDate,
      'appointmentDateFrom',
      'appointmentDateTo'
    ),
    ...deriveDateRange(noticeDate, 'noticeDateFrom', 'noticeDateTo'),
    ...deriveDateRange(receivedDate, 'receivedDateFrom', 'receivedDateTo'),
  };

  const baseFilters = {
    ...cleanedFilters,
    ...dateScalars,
    pageSize,
    scheduleStartDate: dateRange.startDate.toISOString(),
    scheduleEndDate: dateRange.endDate.toISOString(),
    scheduleTimeZoneId: timeZone,
    status,
    assignedResourceIds: extractIds(
      relevantFilters['assignedResourceIds'] as
        | MultiSelectOption[]
        | string[]
        | undefined
    ),
    qualifiedResourceIds: extractIds(
      relevantFilters['qualifiedResourceIds'] as
        | MultiSelectOption[]
        | string[]
        | undefined
    ),
    // Map UI filter names to API-expected keys
    // (matches what schedule.service.ts hasResourceFilters checks for)
    assignedResourceTeamIds: teams,
    assignedResourceOfficeIds: offices,
    assignedResourceGroupIds: groups,
    assignedResourceRoleIds: roles,
    ...(resourceAttributes.length > 0 ? { resourceAttributes } : {}),
    // Employment type → boolean isContingent (+ optional contingentCompanies).
    // Omit the keys entirely when unset or when Staff is picked without
    // companies — mirrors v1's ResourcesList transform.
    ...(isContingent !== undefined ? { isContingent } : {}),
    ...(isContingent === true &&
    Array.isArray(contingentCompanies) &&
    contingentCompanies.length > 0
      ? { contingentCompanies }
      : {}),
    hideCancelledTasks: false,
  };

  // If user is adjuster, filter to only their resources
  if (isAdjuster && currentUserResourceId) {
    return {
      ...baseFilters,
      assignedResourceIds: [currentUserResourceId],
    } as ScheduleFilters;
  }

  return baseFilters as ScheduleFilters;
}
